import React, {useEffect, useState, useMemo, useRef} from 'react';
import {useTranslations} from "../../lang";
import {Button, Col, Drawer, Row, Spin, DatePicker, Select, Tooltip, Space} from "antd";
import api from "../../api";
import dayjs from "dayjs";
import weekOfYear from 'dayjs/plugin/weekOfYear';
import {PlusOutlined, WarningOutlined, DownOutlined, RightOutlined} from "@ant-design/icons";
import './style.css'
import PlantingDrawer from "../../Components/PlantingDrawer";
import CropImage from "../../Components/CropImage";
import PlantingModal from "../../Components/PlantingModal";
import {useAuth} from "../../auth";

dayjs.extend(weekOfYear);

export default function CropPlan() {
    const {translate} = useTranslations()
    const {company} = useAuth()

    const [isLoading, setIsLoading] = useState(true)
    const [plantings, setPlantings] = useState([])
    const [plantingDrawerOpen, setPlantingDrawerOpen] = useState(false)
    const [selectedPlanting, setSelectedPlanting] = useState(null)
    const [plantingModalOpen, setPlantingModalOpen] = useState(false)
    const [expandedGroups, setExpandedGroups] = useState({})
    const [gardens, setGardens] = useState([])

    // Refs for synchronized scrolling
    const headerScrollRef = useRef(null)
    const tableBodyRef = useRef(null)

    // Date range state
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('year'),
        dayjs().endOf('year')
    ])

    function loadData() {
        setIsLoading(true)
        Promise.all([
            api.getCropPlan(),
            api.gardens()
        ]).then(([plantingsData, gardensData]) => {
            setPlantings(plantingsData)
            setGardens(gardensData)
            // Initialize all groups as expanded
            const grouped = groupPlantingsByCrop(plantingsData)
            const initialExpanded = {}
            grouped.forEach((group, idx) => {
                initialExpanded[idx] = true
            })
            setExpandedGroups(initialExpanded)
        }).finally(() => {
            setIsLoading(false)
        })
    }

    useEffect(() => {
        loadData()
    }, []);

    // Synchronized horizontal scrolling
    const handleTimelineScroll = (e) => {
        const scrollLeft = e.target.scrollLeft
        if (headerScrollRef.current) {
            headerScrollRef.current.scrollLeft = scrollLeft
        }
        // Sync all row timelines
        const rows = tableBodyRef.current?.querySelectorAll('.cropplan-timeline-col')
        rows?.forEach(row => {
            if (row !== e.target) {
                row.scrollLeft = scrollLeft
            }
        })
    }

    const toggleGroup = (groupIdx) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupIdx]: !prev[groupIdx]
        }))
    }

    const expandAll = () => {
        const newExpanded = {}
        groupedPlantings.forEach((_, idx) => {
            newExpanded[idx] = true
        })
        setExpandedGroups(newExpanded)
    }

    const collapseAll = () => {
        const newExpanded = {}
        groupedPlantings.forEach((_, idx) => {
            newExpanded[idx] = false
        })
        setExpandedGroups(newExpanded)
    }

    // Calculate granularity based on date range
    const granularity = useMemo(() => {
        if (!dateRange[0] || !dateRange[1]) return 'month'
        const months = dateRange[1].diff(dateRange[0], 'month')
        return months <= 6 ? 'week' : 'month'
    }, [dateRange])

    // Generate periods based on granularity
    const periods = useMemo(() => {
        if (!dateRange[0] || !dateRange[1]) return []

        const result = []
        let current = dateRange[0].startOf(granularity)
        const end = dateRange[1].endOf(granularity)

        while (current.isBefore(end) || current.isSame(end, granularity)) {
            result.push(current)
            current = current.add(1, granularity)
        }

        return result
    }, [dateRange, granularity])

    // Group plantings by crop name and variety
    function groupPlantingsByCrop(plantings) {
        const groups = {}
        plantings.forEach(planting => {
            const crop = planting.crop
            const key = `${crop.name}${crop.variety ? ` - ${crop.variety}` : ''}`
            if (!groups[key]) {
                groups[key] = {
                    crop: crop,
                    plantings: []
                }
            }
            groups[key].plantings.push(planting)
        })
        // Sort groups by crop name
        return Object.keys(groups).sort().map(key => groups[key])
    }

    function getPlantingAmount(planting) {
        if (planting.desired_amount) {
            return planting.desired_amount
        }
        if (planting.rows && planting.plants_per_row) {
            return planting.rows * planting.plants_per_row
        }
        return 0
    }

    function getBedLabel(planting) {
        if (planting.bed) {
            if (planting.bed.garden) {
                return `${planting.bed.garden.name}, ${planting.bed.name}`
            }
            return planting.bed.name
        }
        return null
    }

    // Check if planting is in a given period
    function inBed(planting, period) {
        const transplant = dayjs(planting.transplant_at)
        const harvest = dayjs(planting.harvest_at)
        const periodEnd = period.endOf(granularity)
        return period.isBefore(harvest) && periodEnd.isAfter(transplant)
    }

    function inSeed(planting, period) {
        const sow = dayjs(planting.sow_at)
        const transplant = dayjs(planting.transplant_at)
        const periodEnd = period.endOf(granularity)
        return period.isBefore(transplant) && periodEnd.isAfter(sow)
    }

    function inHarvest(planting, period) {
        const harvest = dayjs(planting.harvest_at)
        const harvestUntil = planting.harvest_until ? dayjs(planting.harvest_until) : harvest.add(1, granularity)
        const periodEnd = period.endOf(granularity)
        return period.isBefore(harvestUntil) && periodEnd.isAfter(harvest)
    }

    // Format period label
    function formatPeriod(period) {
        if (granularity === 'week') {
            return `W${period.week()}`
        }
        return period.format('MMM')
    }

    // Check if planting overlaps with selected date range
    function plantingInRange(planting) {
        if (!dateRange[0] || !dateRange[1]) return true
        const sowAt = planting.sow_at ? dayjs(planting.sow_at) : null
        const harvestAt = planting.harvest_at ? dayjs(planting.harvest_at) : null
        const harvestUntil = planting.harvest_until ? dayjs(planting.harvest_until) : harvestAt

        if (!sowAt && !harvestAt) return true

        const plantingStart = sowAt || harvestAt
        const plantingEnd = harvestUntil || sowAt

        return plantingStart.isBefore(dateRange[1]) && plantingEnd.isAfter(dateRange[0])
    }

    const filteredPlantings = useMemo(() => {
        return plantings.filter(plantingInRange)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plantings, dateRange])

    const filteredTotalPlants = useMemo(() => {
        return filteredPlantings.reduce((sum, p) => sum + getPlantingAmount(p), 0)
    }, [filteredPlantings])

    // Preset date ranges
    const presetRanges = [
        {label: translate("Dit jaar"), value: "this_year"},
        {label: translate("Vorig jaar"), value: "last_year"},
        {label: translate("Komende 3 maanden"), value: "next_3_months"},
        {label: translate("Komende 6 maanden"), value: "next_6_months"},
        {label: translate("Komende 12 maanden"), value: "next_12_months"},
    ]

    function setPresetRange(preset) {
        const now = dayjs()
        switch (preset) {
            case 'this_year':
                setDateRange([now.startOf('year'), now.endOf('year')])
                break
            case 'last_year':
                setDateRange([now.subtract(1, 'year').startOf('year'), now.subtract(1, 'year').endOf('year')])
                break
            case 'next_3_months':
                setDateRange([now.startOf('month'), now.add(3, 'month').endOf('month')])
                break
            case 'next_6_months':
                setDateRange([now.startOf('month'), now.add(6, 'month').endOf('month')])
                break
            case 'next_12_months':
                setDateRange([now.startOf('month'), now.add(12, 'month').endOf('month')])
                break
            default:
                break
        }
    }

    if (isLoading) {
        return <Spin />
    }

    const groupedPlantings = groupPlantingsByCrop(filteredPlantings)

    return (
        <div className="cropplan-page">
            <Row className="cropplan-header" align="middle" gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <h1 className="cropplan-title">{translate('Teelplan')}</h1>
                    <div className="cropplan-stats">
                        <span className="cropplan-stat">
                            {filteredPlantings.length} {filteredPlantings.length === 1 ? translate('aanplant') : translate('aanplanten')}
                        </span>
                        <span className="cropplan-stat-divider">•</span>
                        <span className="cropplan-stat">
                            {filteredTotalPlants} {translate('planten totaal')}
                        </span>
                    </div>
                </Col>
                <Col xs={24} md={10}>
                    <div className="cropplan-filters">
                        <Select
                            placeholder={translate("Periode")}
                            style={{width: 180}}
                            onChange={setPresetRange}
                            options={presetRanges}
                            allowClear
                        />
                        <DatePicker.RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates || [dayjs().startOf('year'), dayjs().endOf('year')])}
                            format="DD-MM-YYYY"
                            style={{flex: 1}}
                        />
                    </div>
                </Col>
                <Col xs={24} md={6} className="text-right">
                    <Button type="primary" onClick={() => setPlantingModalOpen(true)}>
                        <PlusOutlined /> {translate('Toevoegen')}
                    </Button>
                </Col>
            </Row>

            {/* Legend at top */}
            <div className="cropplan-legend cropplan-legend-top">
                <div className="cropplan-legend-item">
                    <div className="cropplan-legend-color cropplan-period-seed"></div>
                    <span>{translate('Voorzaaien')}</span>
                </div>
                <div className="cropplan-legend-item">
                    <div className="cropplan-legend-color cropplan-period-bed"></div>
                    <span>{translate('In bed')}</span>
                </div>
                <div className="cropplan-legend-item">
                    <div className="cropplan-legend-color cropplan-period-harvest"></div>
                    <span>{translate('Oogsten')}</span>
                </div>
                <div className="cropplan-legend-spacer"></div>
                <Space>
                    <Button size="small" onClick={expandAll}>{translate('Open alles')}</Button>
                    <Button size="small" onClick={collapseAll}>{translate('Sluit alles')}</Button>
                </Space>
            </div>

            <div className="cropplan-granularity-info">
                {granularity === 'week'
                    ? translate('Weergave per week')
                    : translate('Weergave per maand')}
            </div>

            {groupedPlantings.length === 0 && (
                <p className="cropplan-empty">{translate('Nog geen aanplantingen in je teelplan.')}</p>
            )}

            <div className="cropplan-table">
                {/* Timeline header */}
                <div className="cropplan-timeline-header">
                    <div className="cropplan-fixed-cols">
                        <div className="cropplan-crop-col">{translate('Gewas')}</div>
                        <div className="cropplan-amount-col">#</div>
                        <div className="cropplan-bed-col">{translate('Bed')}</div>
                    </div>
                    <div className="cropplan-timeline-col" ref={headerScrollRef} onScroll={handleTimelineScroll}>
                        <div className="cropplan-periods">
                            {periods.map((period, idx) => (
                                <div
                                    key={idx}
                                    className={`cropplan-period-header ${period.month() === dayjs().month() && period.year() === dayjs().year() ? 'cropplan-period-current' : ''}`}
                                >
                                    {formatPeriod(period)}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Crop groups */}
                <div className="cropplan-table-body" ref={tableBodyRef}>
                    {groupedPlantings.map((group, groupIdx) => {
                        const groupTotal = group.plantings.reduce((sum, p) => sum + getPlantingAmount(p), 0)
                        const isExpanded = expandedGroups[groupIdx] !== false

                        return (
                            <div key={groupIdx} className="cropplan-group">
                                <div className="cropplan-group-row" onClick={() => toggleGroup(groupIdx)}>
                                    <div className="cropplan-fixed-cols">
                                        <div className="cropplan-crop-col cropplan-group-title">
                                            <span className="cropplan-toggle-icon">
                                                {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                            </span>
                                            <CropImage crop={group.crop} width="24px" height="24px" />
                                            <span>{group.crop.name}{group.crop.variety ? ` - ${group.crop.variety}` : ''}</span>
                                        </div>
                                        <div className="cropplan-amount-col cropplan-group-total">
                                            {groupTotal}
                                        </div>
                                        <div className="cropplan-bed-col"></div>
                                    </div>
                                    <div className="cropplan-timeline-col" onScroll={handleTimelineScroll}>
                                        <div className="cropplan-periods">
                                            {periods.map((period, idx) => (
                                                <div key={idx} className="cropplan-period-cell cropplan-period-empty"></div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {isExpanded && group.plantings.map((planting) => {
                                    const hasBed = !!planting.bed
                                    const amount = getPlantingAmount(planting)

                                    return (
                                        <div
                                            key={planting.id}
                                            className={`cropplan-planting-row ${!hasBed ? 'cropplan-no-bed' : ''}`}
                                            onClick={() => {
                                                setSelectedPlanting(planting)
                                                setPlantingDrawerOpen(true)
                                            }}
                                        >
                                            <div className="cropplan-fixed-cols">
                                                <div className="cropplan-crop-col cropplan-planting-indent">
                                                    {!hasBed && (
                                                        <Tooltip title={translate('Geen bed toegewezen')}>
                                                            <WarningOutlined className="cropplan-warning-icon" />
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                <div className="cropplan-amount-col">
                                                    {amount > 0 ? `${amount}x` : '-'}
                                                </div>
                                                <div className="cropplan-bed-col">
                                                    {getBedLabel(planting) || (
                                                        <span className="cropplan-no-bed-label">{translate('geen bed')}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="cropplan-timeline-col" onScroll={handleTimelineScroll}>
                                                <div className="cropplan-periods">
                                                    {periods.map((period, idx) => {
                                                        const isInBed = inBed(planting, period)
                                                        const isInSeed = inSeed(planting, period)
                                                        const isInHarvest = inHarvest(planting, period)

                                                        let className = 'cropplan-period-cell'
                                                        if (isInBed) className += ' cropplan-period-bed'
                                                        else if (isInSeed) className += ' cropplan-period-seed'
                                                        else if (isInHarvest) className += ' cropplan-period-harvest'

                                                        return (
                                                            <div key={idx} className={className}></div>
                                                        )
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>

            <Drawer
                open={plantingDrawerOpen}
                size="large"
                onClose={() => {
                    setPlantingDrawerOpen(false)
                    setSelectedPlanting(null)
                }}
            >
                {selectedPlanting && (
                    <PlantingDrawer
                        gardenId={selectedPlanting.bed?.garden_id || selectedPlanting.garden_id}
                        plantingId={selectedPlanting.id}
                        bedId={selectedPlanting.bed_id}
                        gardens={gardens}
                        onCloseDrawer={() => {
                            setPlantingDrawerOpen(false)
                            setSelectedPlanting(null)
                            loadData()
                        }}
                    />
                )}
            </Drawer>

            <PlantingModal
                open={plantingModalOpen}
                onClose={() => {
                    setPlantingModalOpen(false)
                    loadData()
                }}
                bed={null}
                companyId={company.id}
                onPlantingAdded={() => {
                    setPlantingModalOpen(false)
                    loadData()
                }}
            />
        </div>
    );
}
